/* ==========================================================================
   Product — the PDP's own state, and nothing else.

   ONE source of truth for what is being bought: the native <select name="id">
   inside the product form. The square tiles are the design; this module keeps
   the select agreeing with them. Whatever the form posts therefore comes from a
   real form control, which is why a shopper can never add a variant they did
   not choose — the failure that matters most on this page.

   It writes no cart behaviour. It never calls the Cart API, never opens a
   drawer, and never builds product markup: the price it swaps in was rendered
   by the same Liquid snippet the page already used, and the labels come from
   the locale files through data attributes. Phase 7 owns the cart and enhances
   the same form through [data-atc].

   Progressive enhancement throughout. Without this module the tiles are not
   shown at all, the select carries the choice, and the page still sells.
   ========================================================================== */

const parseJSON = (node) => {
  if (!node) return null;
  try { return JSON.parse(node.textContent); } catch { return null; }
};

/* ---- variants ------------------------------------------------------------ */

class EmoreVariants extends HTMLElement {
  connectedCallback() {
    this.root = this.closest('[data-product-root]');
    this.select = this.querySelector('[data-variant-input]');
    this.variants = parseJSON(this.root && this.root.querySelector('[data-variant-data]'));
    if (!this.root || !this.select || !this.variants) return;

    this.form = this.closest('form');
    this.qty = this.form && this.form.querySelector('.qty__input');
    this.qtyStatus = this.form && this.form.querySelector('[data-qty-status]');
    this.error = this.form && this.form.querySelector('[data-form-error]');

    this.onChange = this.onChange.bind(this);
    this.addEventListener('change', this.onChange);

    /* The server already resolved ?variant=; adopt its answer rather than
       overriding a valid selection with the first available one. */
    this.apply(this.current(), { push: false });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
  }

  /* Each option group is one radio name, in the product's own option order. */
  get groups() {
    return [...this.querySelectorAll('.opts')];
  }

  chosen() {
    return this.groups.map((group) => {
      const picked = group.querySelector('input:checked');
      return picked ? picked.value : null;
    });
  }

  current() {
    const id = Number(this.select.value);
    return this.variants.find((v) => v.id === id) || null;
  }

  match(values) {
    if (values.some((v) => v === null)) return null;
    return this.variants.find((v) =>
      v.options.length === values.length && v.options.every((o, i) => o === values[i])
    ) || null;
  }

  onChange(event) {
    if (!event.target.matches('.opt__input')) return;
    const values = this.chosen();
    const variant = this.match(values);

    /* A combination Shopify has no variant for is the shopper's question to
       answer, so the group they just touched is marked and the message sits
       where the decision is — never a page-level banner. */
    const group = event.target.closest('.opts');
    this.groups.forEach((g) => g.classList.remove('is-invalid'));
    if (!variant) {
      if (group) group.classList.add('is-invalid');
      this.showError(this.dataset.unavailableMessage);
      this.setCta(false);
      return;
    }

    this.showError('');
    this.apply(variant, { push: true });
  }

  apply(variant, { push }) {
    if (!variant) return;

    this.select.value = String(variant.id);
    this.syncTiles(variant);
    this.syncPrice(variant);
    this.setCta(variant.available);
    this.syncQuantity(variant);

    /* Selection is not navigation: it replaces the entry rather than stacking
       one per tile press, and a reload still restores the same variant. */
    if (push) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({ variant: variant.id }, '', url);
    }
  }

  syncTiles(variant) {
    this.groups.forEach((group, index) => {
      const value = variant.options[index];
      const input = [...group.querySelectorAll('.opt__input')].find((i) => i.value === value);
      if (input && !input.checked) input.checked = true;
    });
  }

  /* Every price on the page is the same markup from the same snippet. */
  syncPrice(variant) {
    const targets = [
      ...this.root.querySelectorAll('[data-price]'),
      ...document.querySelectorAll('[data-purchase-bar] [data-price]')
    ];
    targets.forEach((el) => { el.innerHTML = variant.price_html; });
  }

  /* Both CTAs are the one control in two places. The labels are the theme's,
     read from the markup Liquid rendered — no copy lives in this file. */
  setCta(available) {
    const buttons = [
      this.root.querySelector('[data-pdp-cta] .btn'),
      document.querySelector('[data-purchase-bar] .btn')
    ].filter(Boolean);

    buttons.forEach((btn) => {
      const source = btn.closest('[data-cta-labels]');
      const label = btn.querySelector('[data-btn-label]');
      const arrow = btn.querySelector('.icon');
      const next = source && (available ? source.dataset.labelAdd : source.dataset.labelSoldOut);
      if (label && next) label.textContent = next;
      btn.disabled = !available;
      /* Sold out is not a forward action, so it carries no arrow. */
      if (arrow) arrow.hidden = !available;
    });
  }

  /* Bounds come from Shopify only. A variant with no reliable maximum gets no
     maximum here — an invented ceiling is worse than none. */
  syncQuantity(variant) {
    if (!this.qty) return;

    this.qty.min = variant.qty_min || 1;
    this.qty.step = variant.qty_step || 1;
    if (variant.qty_max) this.qty.max = variant.qty_max;
    else this.qty.removeAttribute('max');

    const value = Number(this.qty.value) || Number(this.qty.min);
    const capped = variant.qty_max ? Math.min(value, variant.qty_max) : value;
    this.qty.value = String(Math.max(capped, Number(this.qty.min)));

    this.announceLimit(variant);
  }

  announceLimit(variant) {
    if (!this.qtyStatus) return;
    const template = this.qtyStatus.dataset.limitTemplate;
    const atLimit = variant.qty_max && Number(this.qty.value) >= variant.qty_max;
    this.qtyStatus.textContent = atLimit && template
      ? template.replace('__n__', variant.qty_max)
      : '';
  }

  showError(message) {
    if (!this.error) return;
    this.error.textContent = message || '';
  }
}

if (!customElements.get('emore-variants')) {
  customElements.define('emore-variants', EmoreVariants);
}

/* ---- quantity ceiling ----------------------------------------------------
   The stepper owns its own value and emits a native change; this only reports
   when that value has reached a maximum Shopify actually gave us.            */

const form = document.querySelector('[data-product-form]');
if (form) {
  const input = form.querySelector('.qty__input');
  const status = form.querySelector('[data-qty-status]');
  if (input && status) {
    input.addEventListener('change', () => {
      const max = Number(input.max);
      const template = status.dataset.limitTemplate;
      const atLimit = max && Number(input.value) >= max;
      status.textContent = atLimit && template ? template.replace('__n__', max) : '';
    });
  }
}

/* The threshold purchase bar is `threshold-bar.js` — the cart page needs the
   same behaviour, so the behaviour is one shared custom element rather than two
   copies of an IntersectionObserver. This module only keeps its price and CTA
   in step with the selected variant. */

/* ---- recommendations -----------------------------------------------------
   Shopify fills `recommendations` only on a request that asks for them, so the
   section renders itself and this swaps the result in. Liquid rendered every
   pixel of it. A failure leaves the page exactly as it is: a missing rail is
   not worth an error surface.                                                */

class EmoreRecommendations extends HTMLElement {
  async connectedCallback() {
    const url = this.dataset.url;
    if (!url) return;
    try {
      const response = await fetch(url);
      if (!response.ok) return;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = doc.querySelector('[data-recommendations]');
      if (next && next.innerHTML.trim()) this.innerHTML = next.innerHTML;
    } catch {
      /* nothing: the row simply does not appear */
    }
  }
}

if (!customElements.get('emore-recommendations')) {
  customElements.define('emore-recommendations', EmoreRecommendations);
}

export { EmoreVariants, EmoreRecommendations };
